import React from "react";

const ORANGE = "#E06639";

/** Build a short page list. Any total size stays compact.
 *  ≤6 pages → show all
 *  near start → 1 2 3 4 5 6 … last
 *  middle → 1 … cur-1 cur cur+1 … last
 *  near end → 1 … last-5 … last
 */
export function getVisiblePageNumbers(current, total) {
  if (total <= 1) return [1];
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const EDGE = 7; // show first/last block of up to 6 numbers
  const set = new Set();

  if (current <= EDGE - 1) {
    for (let i = 1; i <= EDGE; i++) set.add(i);
    set.add(total);
  } else if (current >= total - (EDGE - 2)) {
    set.add(1);
    for (let i = total - (EDGE - 1); i <= total; i++) set.add(i);
  } else {
    set.add(1);
    set.add(current - 1);
    set.add(current);
    set.add(current + 1);
    set.add(total);
  }

  const sorted = [...set].filter((p) => p >= 1 && p <= total).sort((a, b) => a - b);
  const out = [];
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) out.push("…");
    out.push(sorted[i]);
  }
  return out;
}

const btn = (active, disabled) => ({
  padding: "7px 12px",
  minWidth: 36,
  border: `1px solid ${active ? ORANGE : "#e6eef4"}`,
  background: active ? ORANGE : disabled ? "#f3f4f6" : "#fff",
  color: disabled ? "#b7c2cc" : active ? "#fff" : ORANGE,
  borderRadius: 8,
  cursor: disabled ? "not-allowed" : "pointer",
  fontSize: 12,
  fontWeight: 600,
  fontFamily: "Poppins, sans-serif",
  lineHeight: 1.2,
  flexShrink: 0,
});

/**
 * Compact sticky pagination bar — sits outside the scrolling table card.
 * Orange theme; never renders every page when total is large.
 */
export default function ReportPagination({
  currentPage,
  totalPages,
  onPageChange,
  infoText,
  show = true,
}) {
  if (!show || totalPages <= 1) return null;

  const page = Math.min(Math.max(1, currentPage || 1), totalPages);
  const pages = getVisiblePageNumbers(page, totalPages);

  return (
    <div
      style={{
        flexShrink: 0,
        display: "grid",
        gridTemplateColumns: "1fr auto 1fr",
        alignItems: "center",
        gap: 8,
        padding: "10px 14px",
        marginTop: 10,
        background: "#fff7f3",
        border: "1px solid #f0d9ce",
        borderRadius: 10,
        boxSizing: "border-box",
        position: "sticky",
        bottom: 0,
        zIndex: 4,
      }}
    >
      <div aria-hidden="true" />

      <div
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page === 1}
          style={btn(false, page === 1)}
        >
          Previous
        </button>

        {pages.map((p, idx) =>
          p === "…" ? (
            <span
              key={`e-${idx}`}
              style={{
                padding: "0 4px",
                color: "#9ca3af",
                fontSize: 12,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              …
            </span>
          ) : (
            <button
              key={p}
              type="button"
              onClick={() => onPageChange(p)}
              style={btn(p === page, false)}
            >
              {p}
            </button>
          )
        )}

        <button
          type="button"
          onClick={() => onPageChange(Math.min(totalPages, page + 1))}
          disabled={page === totalPages}
          style={{
            ...btn(true, page === totalPages),
            background: page === totalPages ? "#f3d5c8" : ORANGE,
            borderColor: page === totalPages ? "#f0d9ce" : ORANGE,
            color: "#fff",
            minWidth: 64,
          }}
        >
          Next
        </button>
      </div>

      {infoText ? (
        <span
          style={{
            color: "#6a7d8c",
            fontSize: 12,
            fontFamily: "Poppins, sans-serif",
            fontWeight: 500,
            whiteSpace: "nowrap",
            justifySelf: "end",
            textAlign: "right",
          }}
        >
          {infoText}
        </span>
      ) : (
        <div aria-hidden="true" />
      )}
    </div>
  );
}
